import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Avatar,
    Typography,
    Space,
    Spin,
    Empty,
    Input,
    Select,
    Button,
    message,
    notification,
    Modal
} from 'antd';
import {
    FileOutlined,
    UserOutlined,
    SendOutlined,
    SearchOutlined
} from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../utils/config';
import { trIncludes } from '../../utils/trText';
import { getImageUrls } from '../../utils/poifotoUtils';
import axios from 'axios';
import './FeedWidget.css';

const { Text, Title } = Typography;
const { TextArea } = Input;

const decodeMessage = (text) => {
  if (!text) return '';
  try {
    let processed = text.replace(/\+/g, ' ');
    let decoded = decodeURIComponent(processed);
    const textarea = document.createElement('textarea');
    textarea.innerHTML = decoded;
    decoded = textarea.value;
    return decoded;
  } catch (e) {
    try {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    } catch (e2) {
      return text;
    }
  }
};

const decodeUrlOnly = (text) => {
  if (!text) return '';
  try {
    const processed = text.replace(/\+/g, ' ');
    return decodeURIComponent(processed);
  } catch {
    return text;
  }
};

const AnnouncementBody = ({ message }) => {
  const html = React.useMemo(() => {
    if (!message) return '';
    let h = decodeUrlOnly(message);
    return h.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
  }, [message]);
  return (
    <div
      className="announcement-modal-body"
      style={{ padding: '10px 0' }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const FeedWidget = ({ limit = 10, showTitle = true, showPostForm = true }) => {
    const navigate = useNavigate();
    const { darkMode } = useTheme();
    const { user, getAuthHeaders } = useAuth();
    const [feeds, setFeeds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [posting, setPosting] = useState(false);
    const [newPostText, setNewPostText] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [likedPosts, setLikedPosts] = useState(new Set());
    const [comments, setComments] = useState({});
    const [showComments, setShowComments] = useState({});
    const [newComment, setNewComment] = useState({});
    const [commenting, setCommenting] = useState({});
    const [searchText, setSearchText] = useState('');
    const [users, setUsers] = useState([]);
    const [recipientOptions, setRecipientOptions] = useState([]);
    const [selectedRecipients, setSelectedRecipients] = useState([]);
    const [searchingUsers, setSearchingUsers] = useState(false);
    const [sendMode, setSendMode] = useState('paylasim'); 
    const [showUserSuggestions, setShowUserSuggestions] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [todayAnnouncement, setTodayAnnouncement] = useState(null);
    const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
    const feedListRef = useRef(null);
    const searchTimeoutRef = useRef(null);
    const notificationCheckIntervalRef = useRef(null);
    const lastCheckedAnnouncementIds = useRef(new Set());

    const PP_BASE_URL = 'https://dosya.atmturkiye.com';
    const BUCKET_NAME = 'atmpoint';
    const FOLDER_PATH = 'personel';
    const generateLogoUrl = (user_id) => {
        const img = `${PP_BASE_URL}/${BUCKET_NAME}/${FOLDER_PATH}%2F${user_id}.jpg`;
        return `${PP_BASE_URL}/${BUCKET_NAME}/${FOLDER_PATH}%2F${user_id}.jpg`;
    };
    
    const fetchFeeds = useCallback(async (pageNum = 0) => {
        try {
            if (pageNum === 0) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }
            const response = await axios.get(`${API_BASE_URL}/feed/posts`, {
                headers: getAuthHeaders(),
                params: { page: pageNum, limit: 20 }
            });

            if (response.data.success) {
                const feedsData = response.data.feeds || [];
                const feedActivities = feedsData.map(feed => ({
                    id: feed.id,
                    title: feed.title,
                    description: feed.text,
                    tarih: feed.time,
                    status: feed.likes > 0 ? 'Beğenildi' : 'Yeni',
                    avatar: feed.user_avatar || generateLogoUrl(user.kullanici_no),
                    type: 'feed',
                    likes: feed.likes,
                    ilgili: feed.ilgili,
                    comments: feed.comment || 0,
                    hasFile: !!feed.file_path,
                    filePath: feed.file_path,
                    fileName: feed.file_name,
                    userLiked: feed.user_liked || false,
                    lokasyon: feed.lokasyon,
                    lokasyonResimler: feed.lokasyon_resimler || [],
                    lokasyonResimSayisi: feed.lokasyon_resim_sayisi || 0
                }));
                
                if (pageNum === 0) {
                    setFeeds(feedActivities);
                } else {
                    setFeeds(prev => [...prev, ...feedActivities]);
                }
                
                setHasMore(response.data.has_more);
                setPage(pageNum);

                const likedPostIds = new Set(likedPosts);
                feedActivities.forEach(feed => {
                    if (feed.userLiked) {
                        likedPostIds.add(feed.id);
                    }
                });
                setLikedPosts(likedPostIds);
            } else {
                if (pageNum === 0) {
                    setFeeds([]);
                }
            }
        } catch (error) {
            console.error('Feed yükleme hatası:', error);
            if (error.response?.status === 401) {
                if (pageNum === 0) {
                    setFeeds([]);
                }
            } else {
                if (pageNum === 0) {
                    setFeeds([]);
                }
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [getAuthHeaders, likedPosts]);

    useEffect(() => {
        fetchFeeds(0);
    }, []);

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (notificationCheckIntervalRef.current) {
                clearInterval(notificationCheckIntervalRef.current);
            }
        };
    }, []);

    const getTodayKey = useCallback(() => {
        const today = new Date();
        const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        return `announcement_dismissed_${dateStr}`;
    }, []);

    const isAnnouncementDismissed = useCallback(() => {
        const todayKey = getTodayKey();
        return localStorage.getItem(todayKey) === 'true';
    }, [getTodayKey]);

    const dismissAnnouncement = useCallback(() => {
        const todayKey = getTodayKey();
        localStorage.setItem(todayKey, 'true');
        setShowAnnouncementModal(false);
    }, [getTodayKey]);

    const checkAnnouncements = useCallback(async (showModal = false) => {
        if (!user) {
            return;
        }

        try {
            const response = await axios.get(`${API_BASE_URL}/feed/announcements`, {
                headers: getAuthHeaders()
            });

            if (response.data.success && response.data.announcements) {
                const announcements = response.data.announcements;
                
                const todayAnn = announcements.length > 0 ? announcements[0] : null;
                
                if (todayAnn) {
                    setTodayAnnouncement(todayAnn);
                    if (showModal && !isAnnouncementDismissed()) {
                        setShowAnnouncementModal(true);
                    }
                    if (!lastCheckedAnnouncementIds.current.has(todayAnn.id)) {
                        lastCheckedAnnouncementIds.current.add(todayAnn.id);
                        
                        if ('Notification' in window && Notification.permission === 'granted') {
                            new Notification(todayAnn.title || 'Duyuru', {
                                body: todayAnn.message || '',
                                icon: '/favicon.ico',
                                tag: `announcement-${todayAnn.id}`,
                                requireInteraction: false,
                                silent: false
                            });
                        }
                    }
                } else {
                    setTodayAnnouncement(null);
                }
            }
        } catch (error) {
            console.error('Announcement check error:', error);
        }
    }, [user, getAuthHeaders, API_BASE_URL, isAnnouncementDismissed]);

    useEffect(() => {
        if (!user) return;
        checkAnnouncements(true);
        if ('Notification' in window) {
            if (Notification.permission === 'default') {
                Notification.requestPermission().then(permission => {
                    if (permission === 'granted') {
                        checkAnnouncements(false);
                    }
                });
            } else if (Notification.permission === 'granted') {
                checkAnnouncements(false);
            }
        }

        notificationCheckIntervalRef.current = setInterval(() => {
            checkAnnouncements(false);
        }, 10000);

        return () => {
            if (notificationCheckIntervalRef.current) {
                clearInterval(notificationCheckIntervalRef.current);
            }
        };
    }, [user, checkAnnouncements]);

    const handleScroll = useCallback(() => {
        if (!feedListRef.current || loadingMore || !hasMore) return;
        const { scrollTop, scrollHeight, clientHeight } = feedListRef.current;
        if (scrollTop + clientHeight >= scrollHeight * 0.8) {
            fetchFeeds(page + 1);
        }
    }, [loadingMore, hasMore, page, fetchFeeds]);

    useEffect(() => {
        const feedList = feedListRef.current;
        if (feedList) {
            feedList.addEventListener('scroll', handleScroll);
            return () => feedList.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);
    
    const handlePostSubmit = async () => {
        if (!newPostText.trim()) {
            message.warning('Lütfen bir mesaj yazın');
            return;
        }

        try {
            setPosting(true);
            if (sendMode === 'duyuru') {
                const payload = {
                    title: 'Duyuru',
                    text: newPostText.trim(),
                    recipients: selectedRecipients, 
                };
                const res = await axios.post(`${API_BASE_URL}/feed/announce/send`, payload, {
                    headers: {
                        ...getAuthHeaders(),
                        'Content-Type': 'application/json',
                    },
                });
                if (res.data.success) {
                    message.success('Duyuru gönderildi');
                    notification.success({
                        message: 'Duyuru Gönderildi 📣',
                        description: 'Seçili kullanıcılara duyuru iletildi (e-posta + SMS + bildirim)',
                        duration: 3,
                    });
                    setNewPostText('');
                    setSelectedRecipients([]);
                    setSendMode('paylasim');
                    setTimeout(() => {
                        if (checkAnnouncements) {
                            checkAnnouncements();
                        }
                    }, 3000);
                } else {
                    message.error(res.data.message || 'Duyuru gönderilemedi');
                }
            } else {
                const formData = new FormData();
                formData.append('post_text', newPostText.trim());
                const response = await axios.post(`${API_BASE_URL}/feed/send/posts`, formData, {
                    headers: {
                        ...getAuthHeaders(),
                        'Content-Type': 'multipart/form-data',
                    },
                });
                if (response.data.success) {
                    message.success('Paylaşım başarıyla oluşturuldu!');
                    notification.success({
                        message: 'Paylaşım Oluşturuldu! ✅',
                        description: 'Paylaşımınız başarıyla feed\'e eklendi',
                        duration: 3,
                    });
                    setNewPostText('');
                    await fetchFeeds(0);
                } else {
                    message.error(response.data.message || 'Paylaşım oluşturulamadı');
                }
            }
        } catch (error) {
            console.error('Post creation error:', error);
            if (error.response?.status === 401) {
                message.error('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
            } else {
                message.error('Paylaşım oluşturulurken hata oluştu');
            }
        } finally {
            setPosting(false);
        }
    };

    const searchRecipients = useCallback(async (query) => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(async () => {
            try {
                setSearchingUsers(true);
                const res = await axios.get(`${API_BASE_URL}/feed/users/search`, {
                    headers: getAuthHeaders(),
                    params: { q: query || '', limit: 50 },
                });
                if (res.data.success) {
                    const opts = (res.data.users || []).map(u => ({ value: u.id, label: `${u.name}` }));
                    setRecipientOptions(opts);
                }
            } catch (e) {
                console.error('User search error:', e);
            } finally {
                setSearchingUsers(false);
            }
        }, 300);
    }, [API_BASE_URL, getAuthHeaders]);

    const handleLike = async (postId, currentLikes) => {
        try {
            const isLiked = likedPosts.has(postId);
            const response = await axios.post(`${API_BASE_URL}/feed/like`, {
                feed_id: postId,
                action: isLiked ? 'unlike' : 'like'
            }, {
                headers: getAuthHeaders()
            });

            if (response.data.success) {
                if (response.data.user_liked) {
                    notification.success({
                        message: 'Beğenildi! 👍',
                        description: 'Paylaşımınız beğenildi',
                        duration: 2,
                    });
                }
                setFeeds(prevFeeds =>
                    prevFeeds.map(feed =>
                        feed.id === postId
                            ? { ...feed, likes: response.data.new_likes }
                            : feed
                    )
                );
                setLikedPosts(prev => {
                    const newSet = new Set(prev);
                    if (response.data.user_liked) {
                        newSet.add(postId);
                    } else {
                        newSet.delete(postId);
                    }
                    return newSet;
                });
            }
        } catch (error) {
            console.error('Like error:', error);
            message.error('Beğeni işlemi başarısız');
        }
    };

    const fetchComments = async (postId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/feed/comments/${postId}`, {
                headers: getAuthHeaders()
            });

            if (response.data.success) {
                setComments(prev => ({
                    ...prev,
                    [postId]: response.data.comments
                }));
            }
        } catch (error) {
            console.error('Comments fetch error:', error);
            message.error('Yorumlar yüklenirken hata oluştu');
        }
    };

    const handleCommentSubmit = async (postId) => {
        const commentText = newComment[postId];
        if (!commentText?.trim()) {
            message.warning('Lütfen bir yorum yazın');
            return;
        }

        try {
            setCommenting(prev => ({ ...prev, [postId]: true }));

            const response = await axios.post(`${API_BASE_URL}/feed/comment`, {
                feed_id: postId,
                text: commentText.trim()
            }, {
                headers: getAuthHeaders()
            });

            if (response.data.success) {
                message.success('Yorum başarıyla eklendi!');

                notification.info({
                    message: 'Yeni Yorum 💬',
                    description: 'Paylaşıma yeni bir yorum eklendi',
                    duration: 3,
                });

                setNewComment(prev => ({ ...prev, [postId]: '' }));

                setFeeds(prevFeeds =>
                    prevFeeds.map(feed =>
                        feed.id === postId
                            ? { ...feed, comments: (feed.comments || 0) + 1 }
                            : feed
                    )
                );

                await fetchComments(postId);
            }
        } catch (error) {
            console.error('Comment error:', error);
            message.error('Yorum eklenirken hata oluştu');
        } finally {
            setCommenting(prev => ({ ...prev, [postId]: false }));
        }
    };

    const toggleComments = async (postId) => {
        const isShowing = showComments[postId];
        setShowComments(prev => ({ ...prev, [postId]: !isShowing }));

        if (!isShowing && !comments[postId]) {
            await fetchComments(postId);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');

        return `${day}-${month}-${year} ${hours}.${minutes}`;
    };

    const formatTitle = (title) => {
        if (!title) return title;
        return title.replace(/<[^>]*>/g, '');
    };

    const processMentions = (text) => {
        if (!text) return text;
        let processedText = text.replace(/\/atm\/rapor\/(\d+)/g, '/detay/$1');

        processedText = processedText.replace(/\[([^\]]+)\]/g, (match, ad_soyad) => {
            return `<span class="mention"><a href="#${ad_soyad}">@${ad_soyad}</a></span>`;
        });

        processedText = processedText.replace(/@(\w+)/g, (match, ad_soyad) => {
            return `<span class="mention"><a href="#${ad_soyad}">@${ad_soyad}</a></span>`;
        });

        return processedText;
    };

    // Lokasyon fotoğrafı: postun altında, feed genişliğinde tek (ilk) resim.
    // Backend lokasyonResim'in ilk 6 adını gönderiyor; yalnız ilki kullanılır.
    // Tıklama, başlık/avatar gibi lokasyon detayına götürür (tam galeri orada).
    const renderLokasyonResimleri = (item) => {
        if (!(Number(item.lokasyon) > 0)) return null;
        const resimler = getImageUrls(item.lokasyonResimler, item.lokasyon, { lowQualitySize: '960:540' });
        if (resimler.length === 0) return null;
        const ilk = resimler[0];

        return (
            <div
                className="feed-lokasyon-resim"
                title="Lokasyon detayına git"
                onClick={() => navigate(`/detay/${item.lokasyon}`)}
            >
                <img
                    src={ilk.url}
                    alt="Lokasyon resmi"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                />
            </div>
        );
    };

    const renderFileAttachment = (filePath, fileName) => {
        if (!filePath) return null;

        const fileExtension = filePath.split('.').pop().toLowerCase();
        const displayFileName = fileName || filePath.split('~').pop() || filePath.split('/').pop();

        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension)) {
            return (
                <img
                    src={`${API_BASE_URL}/feed/files/${filePath}`}
                    alt="Post attachment"
                    className="feed-file-image"
                />
            );
        }

        const fileIcons = {
            csv: '📊', doc: '📄', docx: '📄', mp3: '🎵', mp4: '🎬',
            pdf: '📕', ppt: '📊', pptx: '📊', xls: '📈', xlsx: '📈',
            txt: '📝'
        };

        return (
            <a
                href={`${API_BASE_URL}/feed/files/${filePath}`}
                target="_blank"
                rel="noopener noreferrer"
                className="filewrap"
            >
                <div className="fileicon">
                    <span className="file-icon">
                        {fileIcons[fileExtension] || '📁'}
                    </span>
                </div>
                <div className="ftext">
                    <h6>{displayFileName}</h6>
                    <small>Dosyayı indirmek için tıklayın...</small>
                </div>
            </a>
        );
    };

    const handleSearch = (e) => {
        setSearchText(e.target.value);
    };

    const renderComments = (postId) => {
        const postComments = comments[postId] || [];
        const isShowing = showComments[postId];

        if (!isShowing) return null;

        return (
            <div className="comments-section">
                <div className="comments-list">
                    {postComments.map((comment) => (
                        <div key={comment.id} className="comment-item">
                            <Avatar
                                size="small"
                                src={comment.user_avatar || generateLogoUrl(comment.kullanici_no)}
                                icon={<UserOutlined />}
                            />
                            <div className="comment-content">
                                <div className="comment-header">
                                    <Text strong>{comment.user_name}</Text>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        {formatDate(comment.created_at)}
                                    </Text>
                                </div>
                                <Text>{comment.text}</Text>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="comment-form">
                    <Space style={{ width: '100%' }}>
                        <Avatar
                            size="small"
                            src={user?.avatar_url || generateLogoUrl(user?.kullanici_no)}
                            icon={<UserOutlined />}
                        />
                        <Input
                            placeholder="Yorum yazın..."
                            value={newComment[postId] || ''}
                            onChange={(e) => setNewComment(prev => ({ ...prev, [postId]: e.target.value }))}
                            onPressEnter={() => handleCommentSubmit(postId)}
                            style={{ flex: 1 }}
                        />
                        <Button
                            type="primary"
                            size="small"
                            loading={commenting[postId]}
                            onClick={() => handleCommentSubmit(postId)}
                        >
                            Gönder
                        </Button>
                    </Space>
                </div>
            </div>
        );
    };

    return (
        <Card
            className={`feed-widget ${darkMode ? 'dark-mode' : ''}`}
        >
            {/* Search Input */}
            <div style={{ marginTop: '16px', marginBottom: '16px', padding: '0 20px' }}>
                <Input
                    placeholder="Ara..."
                    value={searchText}
                    onChange={handleSearch}
                    allowClear
                    prefix={<SearchOutlined />}
                    className={darkMode ? 'dark-mode' : ''}
                />
            </div>
            {/* Post Creation Form */}
            {showPostForm && (
                <Card
                    size="small"
                    className={`feed-post-form ${darkMode ? 'dark-mode' : ''}`}
                >
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Space style={{ width: '100%' }}>
                            <Avatar
                                src={user?.avatar_url || generateLogoUrl(user?.kullanici_no)}
                                icon={<UserOutlined />}
                            />
                            <Text strong>{user?.ad_soyad || 'Kullanıcı'}</Text>
                        </Space>

                        {/* Post text + recipient selector */}
                        <TextArea
                            value={newPostText}
                            onChange={(e) => {
                                const value = e.target.value;
                                setNewPostText(value);
                                const cursorPos = e.target.selectionStart;
                                const textBeforeCursor = value.substring(0, cursorPos);
                                const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

                                if (mentionMatch) {
                                    setShowUserSuggestions(true);
                                } else {
                                    setShowUserSuggestions(false);
                                }
                            }}
                            placeholder="Ne düşünüyorsunuz?"
                            autoSize={{ minRows: 2, maxRows: 4 }}
                            maxLength={1000}
                            showCount
                            style={{}}
                        />

                        <div style={{ display: 'flex', gap: 18 }}>
                            <Select
                                value={sendMode}
                                onChange={(v) => setSendMode(v)}
                                style={{ width: 160, marginTop: 20 }}
                                options={[
                                    { value: 'paylasim', label: 'Paylaşım' },
                                    { value: 'duyuru', label: 'Duyuru' },
                                ]}
                            />
                            <Select
                                mode="multiple"
                                allowClear
                                style={{ flex: 1, marginTop: 20 }}
                                placeholder="Kişi seç"
                                value={selectedRecipients}
                                onChange={(vals) => setSelectedRecipients(vals)}
                                showSearch
                                filterOption={(input, option) =>
                                    trIncludes(option?.label || '', input)
                                }
                                onSearch={(v) => {
                                    searchRecipients(v);
                                }}
                                onDropdownVisibleChange={(open) => {
                                    if (open && recipientOptions.length === 0) {
                                        searchRecipients('');
                                    }
                                }}
                                notFoundContent={searchingUsers ? 'Yükleniyor...' : 'Kişi bulunamadı'}
                                options={recipientOptions}
                                disabled={sendMode !== 'duyuru'}
                                optionFilterProp="label"
                            />
                        </div>

                        {/* User Suggestions for Mentions */}
                        {showUserSuggestions && (
                            <div className="user-suggestions">
                                {users.map(user => (
                                    <div
                                        key={user.id}
                                        className="user-suggestion"
                                        onClick={() => {
                                            const cursorPos = document.querySelector('textarea').selectionStart;
                                            const textBeforeCursor = newPostText.substring(0, cursorPos);
                                            const textAfterCursor = newPostText.substring(cursorPos);
                                            const beforeMention = textBeforeCursor.replace(/@(\w*)$/, '');

                                            setNewPostText(beforeMention + `@${user.ad_soyad} ` + textAfterCursor);
                                            setShowUserSuggestions(false);
                                        }}
                                    >
                                        <Avatar size="small" icon={<UserOutlined />} />
                                        <div>
                                            <div className="ad_soyad">@{user.ad_soyad}</div>
                                            <div className="display-name">{user.display_name}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {selectedFile && (
                            <div className="file-attachment">
                                <div className="file-info">
                                    <FileOutlined className="file-icon" />
                                    <div>
                                        <div className="file-name">{selectedFile.name}</div>
                                        <div className="file-size">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</div>
                                    </div>
                                </div>
                                <Button
                                    size="small"
                                    type="text"
                                    onClick={() => setSelectedFile(null)}
                                >
                                    ×
                                </Button>
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                            <Button
                                type="primary"
                                icon={<SendOutlined />}
                                onClick={handlePostSubmit}
                                loading={posting}
                                disabled={!newPostText.trim()}
                            >
                                {sendMode === 'duyuru' ? 'Duyuru Gönder' : 'Paylaş'}
                            </Button>
                        </div>
                    </Space>
                </Card>
            )}

            {/* Feed List */}
            {loading ? (
                <div className="feed-loading">
                    <Spin size="large" />
                </div>
            ) : (
                <div className="feed-list" ref={feedListRef} style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {(() => {
                        const filteredFeeds = searchText.trim()
                            ? feeds.filter(feed => (
                                trIncludes(feed.description || '', searchText) ||
                                trIncludes(feed.title || '', searchText)
                            ))
                            : feeds;

                        return (
                            <>
                                {filteredFeeds.length === 0 ? (
                                    <div className="feed-empty">
                                        <Empty description={searchText ? "Arama sonucu bulunamadı" : "Henüz paylaşım yok"} />
                                    </div>
                                ) : (
                                    filteredFeeds.map((item) => (
                            <div key={item.id} className="feed">
                                <img
                                    className="pp"
                                    src={generateLogoUrl(item.ilgili)}
                                    alt={item.title}
                                    style={Number(item.lokasyon) > 0 ? { cursor: 'pointer' } : undefined}
                                    onClick={Number(item.lokasyon) > 0 ? () => navigate(`/detay/${item.lokasyon}`) : undefined}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Ccircle cx='20' cy='20' r='20' fill='%23c8ccd4'/%3E%3Ccircle cx='20' cy='15' r='6.5' fill='%23fff'/%3E%3Cpath d='M8 34c0-7 24-7 24 0z' fill='%23fff'/%3E%3C/svg%3E";
                                    }}
                                />
                                <div className="feedbody">
                                    <h6>
                                        {Number(item.lokasyon) > 0 ? (
                                            <span
                                                style={{ cursor: 'pointer', color: '#1890ff' }}
                                                onClick={() => navigate(`/detay/${item.lokasyon}`)}
                                                title="Lokasyon detayına git"
                                            >
                                                {formatTitle(decodeMessage(item.title))}
                                            </span>
                                        ) : (
                                            formatTitle(decodeMessage(item.title))
                                        )}
                                    </h6>
                                    <div className="tarih">{formatDate(item.tarih)}</div>

                                    <p
                                        dangerouslySetInnerHTML={{
                                            __html: processMentions(decodeMessage(item.description))
                                        }}
                                    />

                                    {renderLokasyonResimleri(item)}

                                    {item.filePath && renderFileAttachment(item.filePath, item.fileName)}

                                    <div className="likebar">
                                        <button
                                            className={`btn btn-default feedbutton ${likedPosts.has(item.id) ? 'liked' : ''}`}
                                            onClick={() => handleLike(item.id, item.likes || 0)}
                                            type="button"
                                        >
                                            {item.likes || 0} <i className="fas fa-thumbs-up"></i>
                                        </button>
                                        <button
                                            className="btn btn-default feedbutton"
                                            type="button"
                                            onClick={() => toggleComments(item.id)}
                                        >
                                            {item.comments || 0} <i className="fas fa-comment-alt"></i>
                                        </button>
                                    </div>
                                    {renderComments(item.id)}
                                </div>
                            </div>
                            ))
                                )}
                                
                                {/* Loading more indicator */}
                                {loadingMore && (
                                    <div style={{ textAlign: 'center', padding: '20px' }}>
                                        <Spin />
                                    </div>
                                )}
                                
                                {/* End of list indicator */}
                                {!hasMore && feeds.length > 0 && (
                                    <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                        <Text type="secondary">Tüm paylaşımlar yüklendi</Text>
                                    </div>
                                )}
                            </>
                        );
                    })()}
                </div>
            )}
            
            {/* Announcement Modal */}
            <Modal
                title={decodeMessage(todayAnnouncement?.title) || 'Duyuru'}
                open={showAnnouncementModal}
                onCancel={() => setShowAnnouncementModal(false)}
                footer={[
                    <Button key="dismiss" onClick={dismissAnnouncement}>
                        Bir Daha Gösterme
                    </Button>,
                    <Button key="close" type="primary" onClick={() => setShowAnnouncementModal(false)}>
                        Tamam
                    </Button>
                ]}
                width={600}
                className={darkMode ? 'dark-mode' : ''}
            >
                <AnnouncementBody message={todayAnnouncement?.message} />
            </Modal>
        </Card>
    );
};

export default FeedWidget;